import React from "react"
import { Horizontal, Vertical } from "@/components/standard/BFLayout"
import { H3, H6, Small } from "@/components/standard/BFText"
import { Card, CardContent, CardFooter } from "@/components/reusables/ui/card"
import { Badge } from "@/components/reusables/ui/badge"
import { Image } from "expo-image"
import { Linking, Pressable } from "react-native"
import { trackTrustedPartnerClicked } from "@/analytics/events/trustedPartners"
import type { TrustedPartnerFragment } from "@/generated/graphql"
import IconPhone from "phosphor-react-native/src/icons/Phone"
import IconGlobe from "phosphor-react-native/src/icons/Globe"
import IconEnvelope from "phosphor-react-native/src/icons/Envelope"
import { Separator } from "@/components/reusables/ui/separator"

interface TrustedPartnerCardProps {
  partner: TrustedPartnerFragment
}

export const TrustedPartnerCard: React.FC<TrustedPartnerCardProps> = ({
  partner,
}) => {
  const handleAction = (type: "web" | "phone" | "email") => {
    let url = ""
    let functionName = ""

    switch (type) {
      case "web":
        url = partner.webLink || ""
        functionName = "visit website"
        break
      case "phone":
        url = `tel:${partner.phone}`
        functionName = "call partner"
        break
      case "email":
        url = `mailto:${partner.email}`
        functionName = "email partner"
        break
    }

    if (url) {
      trackTrustedPartnerClicked(
        `trusted-partner-${type}-cta`,
        functionName,
        partner
      )
      void Linking.openURL(url)
    }
  }

  const renderPhoneCTA = () => (
    <Pressable
      onPress={() => {
        handleAction("phone")
      }}
      testID="trusted-partner-phone"
      hitSlop={16}
    >
      <Horizontal className="gap-1 items-center">
        <IconPhone size={16} color="#1d4261" />
        <H6 color="primaryLight" className="underline">
          {partner.phone}
        </H6>
      </Horizontal>
    </Pressable>
  )

  const renderWebCTA = () => (
    <Pressable
      onPress={() => {
        handleAction("web")
      }}
      testID="trusted-partner-website"
      hitSlop={16}
    >
      <Horizontal className="gap-1 items-center">
        <IconGlobe size={16} color="#1d4261" />
        <H6 color="primaryLight" className="underline">
          {partner.ctaText || "Learn More"}
        </H6>
      </Horizontal>
    </Pressable>
  )

  const renderEmailCTA = () => (
    <Pressable
      onPress={() => {
        handleAction("email")
      }}
      testID="trusted-partner-email"
      hitSlop={16}
    >
      <Horizontal className="gap-1 items-center">
        <IconEnvelope size={16} color="#1d4261" />
        <H6 color="primaryLight" className="underline">
          {partner.email}
        </H6>
      </Horizontal>
    </Pressable>
  )

  const renderCTAFooter = () => {
    const availableCTAs = []

    if (partner.phone) {
      availableCTAs.push(renderPhoneCTA())
    } else if (partner.email) {
      availableCTAs.push(renderEmailCTA())
    }
    if (partner.webLink) availableCTAs.push(renderWebCTA())

    if (availableCTAs.length === 0) {
      // No CTAs: Use fallback text
      return (
        <CardFooter className="px-4 py-3">
          <Horizontal className="justify-center w-full">
            <H6>{partner.ctaText || "Ask your Backflip lender for details"}</H6>
          </Horizontal>
        </CardFooter>
      )
    }

    if (availableCTAs.length === 1) {
      // Single CTA
      return (
        <CardFooter className="px-4 py-3">
          <Horizontal className="justify-center w-full">
            {availableCTAs[0]}
          </Horizontal>
        </CardFooter>
      )
    }

    // Multiple CTAs with separators
    return (
      <CardFooter className="px-4 py-3">
        <Horizontal className="gap-2 items-center justify-center w-full">
          <Horizontal className="flex-1 justify-center">
            {availableCTAs[0]}
          </Horizontal>
          <Separator orientation={"vertical"} />
          <Horizontal className="flex-1  justify-center">
            {availableCTAs[1]}
          </Horizontal>
        </Horizontal>
      </CardFooter>
    )
  }

  return (
    <Card className="bg-card shadow-sm">
      {/* Header section with logo, name, and category tag */}
      <CardContent className="p-4 pb-0">
        <Horizontal className="items-center gap-3">
          {partner.logoUrl && (
            <Image
              source={{ uri: partner.logoUrl }}
              style={{
                width: 48,
                height: 48,
                borderRadius: 48,
              }}
              contentFit="contain"
            />
          )}

          <Vertical className="flex-1 gap-1">
            <H3 color="primary">{partner.name}</H3>
          </Vertical>
          {partner.category && (
            <Badge variant="secondary" className="self-start">
              <Small>{partner.category}</Small>
            </Badge>
          )}
        </Horizontal>
      </CardContent>

      <CardContent className="p-4 border-0">
        <Small>{partner.description}</Small>
      </CardContent>

      {renderCTAFooter()}
    </Card>
  )
}
